import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { IoMdClose } from "react-icons/io";
import { BsCalendar } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";
import BookForm from "../BookForm";
import { RoomContext } from "../../context/RoomContext";

// Breakpoint lg de tailwind.config.js
const DESKTOP_QUERY = "(min-width: 960px)";

const fmt = (d) =>
  d ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}` : "";

/**
 * Buscador de reservas de la home (estilo Gran Meliá).
 * - Desktop: tarjeta blanca flotando sobre la parte inferior del video.
 *   Al hacer scroll y llegar al header, se fija pegada justo debajo del
 *   navbar. Al tocar cualquier campo en ese estado, la página se oscurece
 *   y queda solo el buscador destacado, con una X para cerrar.
 * - Mobile: tarjeta con filas montada sobre el final del hero. Al pasarla,
 *   aparece una barra fija abajo con un botón que abre el buscador en una
 *   hoja deslizante desde abajo.
 */
const StickyBookingBar = () => {
  const placeholderRef = useRef(null);
  const { checkInDate, checkOutDate, adults, kids } = useContext(RoomContext);

  const [fixed, setFixed] = useState(false); // desktop: pegada al navbar
  const [expanded, setExpanded] = useState(false); // desktop: modo destacado
  const [headerHeight, setHeaderHeight] = useState(0);
  const [mobileBar, setMobileBar] = useState(false); // mobile: barra inferior
  const [sheetOpen, setSheetOpen] = useState(false); // mobile: hoja abierta

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const header = document.getElementById("header");

    const update = () => {
      const el = placeholderRef.current;
      if (!el) return;
      const hH = header ? header.offsetHeight : 0;
      const rect = el.getBoundingClientRect();
      setHeaderHeight(hH);
      setFixed(mq.matches && rect.top <= hH);
      setMobileBar(!mq.matches && rect.bottom < hH);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    mq.addEventListener?.("change", update);

    // El header cambia de alto al hacer scroll (padding): seguirlo.
    // border-box porque solo cambia el padding, no el contenido.
    let ro;
    if (header && "ResizeObserver" in window) {
      ro = new ResizeObserver(update);
      ro.observe(header, { box: "border-box" });
    }

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      mq.removeEventListener?.("change", update);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!fixed) setExpanded(false);
  }, [fixed]);

  useEffect(() => {
    if (!mobileBar) setSheetOpen(false);
  }, [mobileBar]);

  const close = useCallback(() => {
    setExpanded(false);
    setSheetOpen(false);
  }, []);

  // Escape cierra
  useEffect(() => {
    if (!expanded && !sheetOpen) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, sheetOpen, close]);

  // Clases en body para que el layout reaccione (WhatsApp, scroll lock)
  useEffect(() => {
    const b = document.body.classList;
    b.toggle("booking-bar-fixed", fixed);
    b.toggle("booking-bar-expanded", expanded);
    b.toggle("booking-bar-mobile", mobileBar);
    b.toggle("booking-sheet-open", sheetOpen);
    document.body.style.overflow = sheetOpen ? "hidden" : "";
    return () => {
      b.remove("booking-bar-fixed", "booking-bar-expanded", "booking-bar-mobile", "booking-sheet-open");
      document.body.style.overflow = "";
    };
  }, [fixed, expanded, mobileBar, sheetOpen]);

  const open = () => {
    if (fixed && !expanded) setExpanded(true);
  };

  const barClass = !fixed
    ? "container mx-auto"
    : expanded
    ? "fixed left-0 right-0 z-[70] booking-dock booking-dock--expanded"
    : "fixed left-0 right-0 z-40 booking-dock animate-bookingBarIn";

  const datesLabel =
    checkInDate && checkOutDate
      ? `${fmt(checkInDate)} → ${fmt(checkOutDate)}`
      : "Elegí tus fechas";
  const guestsLabel = `${adults}${kids && !/^0/.test(kids) ? ` · ${kids}` : ""}`;

  return (
    // Placeholder: reserva el espacio cuando la tarjeta pasa a "fixed"
    // y la hace flotar sobre la parte inferior del hero.
    <div ref={placeholderRef} className="relative -mt-14 lg:-mt-[112px] lg:h-[64px]">
      {/* Fondo oscuro (desktop destacado / mobile hoja abierta) */}
      {((fixed && expanded) || sheetOpen) && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 animate-fadeIn"
          onClick={close}
          aria-hidden="true"
        />
      )}

      <div
        id="booking-bar"
        className={barClass}
        style={fixed ? { top: expanded ? headerHeight + 20 : headerHeight } : undefined}
        onFocusCapture={open}
        onMouseDownCapture={open}
      >
        <div className={fixed ? "container mx-auto relative" : ""}>
          <div className={`booking-card ${fixed ? "booking-card--docked" : ""}`}>
            <BookForm compact />
          </div>

          {fixed && expanded && (
            <button
              type="button"
              onClick={close}
              aria-label="Cerrar buscador"
              className="absolute -top-3 -right-3 lg:-right-12 lg:top-1/2 lg:-translate-y-1/2 w-10 h-10 rounded-full bg-white text-primary shadow-lg flex items-center justify-center hover:bg-accent hover:text-white transition-colors animate-fadeIn"
            >
              <IoMdClose className="text-2xl" />
            </button>
          )}
        </div>
      </div>

      {/* ---- Mobile: barra inferior fija ---- */}
      {mobileBar && !sheetOpen && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-white border-t border-gray-100 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] animate-barUp">
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="w-full flex items-center justify-between gap-4 px-5 py-3"
          >
            <span className="flex items-center gap-3 text-left">
              <BsCalendar className="text-accent text-lg flex-shrink-0" />
              <span className="flex flex-col">
                <span className="font-tertiary uppercase tracking-[3px] text-[11px] text-gray-400">
                  Reservá tu estadía
                </span>
                <span className="text-[15px] text-primary leading-tight">
                  {datesLabel} · {guestsLabel}
                </span>
              </span>
            </span>
            <span className="btn btn-primary flex-none h-11 px-5 text-[13px] tracking-[2px]">
              <FiSearch className="mr-2" />
              Buscar
            </span>
          </button>
        </div>
      )}

      {/* ---- Mobile: hoja deslizante con el buscador ---- */}
      {sheetOpen && (
        <div className="booking-sheet lg:hidden fixed inset-x-0 bottom-0 z-[70] bg-white rounded-t-2xl shadow-2xl animate-sheetIn px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-3" />
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="font-tertiary uppercase tracking-[4px] text-[11px] text-accent">
                Hotel Boutique
              </div>
              <h3 className="font-primary text-2xl leading-tight">Reservá tu estadía</h3>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Cerrar buscador"
              className="w-10 h-10 -mr-2 rounded-full flex items-center justify-center text-primary hover:bg-gray-100"
            >
              <IoMdClose className="text-2xl" />
            </button>
          </div>
          <div className="booking-card">
            <BookForm compact />
          </div>
        </div>
      )}
    </div>
  );
};

export default StickyBookingBar;
